import type { ShelfRegion } from '@cuito/types';
import { apiClient } from '../lib/apiClient';

/**
 * 网格里的一个格子（`04-api-contract.md` §7.1 的 `shelves[].items[]`）。
 *
 * 这是**渲染聚合**：带价格、可用量、淘宝链接等展示字段，只出现在网格响应里。
 * 货架资源自身的 `items` 是另一套形状（见下方 `ShelfItemView`）——两者不可互换，
 * 所以用两个类型名而不是一个 `ShelfView`。
 */
export interface GridCellView {
  shelfItemId: string;
  /** `PRODUCT`＝单品格子，`PROMOTION`＝方案格子；与 productId/schemeId 恰有其一非空一一对应 */
  kind: 'PRODUCT' | 'PROMOTION';
  productId: string | null;
  schemeId: string | null;
  /** **仅方案格子非空** */
  scheme: {
    id: string;
    title: string;
    priceCents: number;
    originalTotalCents: number;
    itemCount: number;
  } | null;
  productCode: string | null;
  name: string;
  /** 字符串十进制（如 `"45.00"`），与 §1.7 的金额口径一致 */
  price: string;
  imageUrl: string | null;
  previewVideoUrl: string | null;
  isAlbum: boolean;
  /** 单品格子的摆放数量；**方案格子恒为 null**（方案不持有库存） */
  shelfQuantity: number | null;
  availableQty: number;
  purchaseLimit: number | null;
  sortOrder: number;
  taobaoUrl: string | null;
  soldOut: boolean;
}

/** 网格里的一个货架区域（契约 §7.1 的 `shelves[]`，服务端已按 sortOrder 升序、方案格子置顶） */
export interface GridShelfView extends ShelfRegion {
  shelfId: string;
  /** 位置标识快照，如 `A1`（单格）或 `A1-B2`（合并区域） */
  code: string;
  label: string | null;
  iconUrl: string | null;
  soldOut: boolean;
  items: GridCellView[];
}

/** `GET /locations/:id/grid`（契约 §7.1） */
export interface LocationGridView {
  location: { id: string; name: string; type: string; active: boolean };
  grid: { rows: number; cols: number; cellSize: number };
  /** **只返回货架，不返回空洞**（契约 §7.1「关键语义」） */
  shelves: GridShelfView[];
}

/**
 * `ShelfItem` 本体（契约 §7.5 的 201）。
 *
 * **注意与 `GridCellView` 的区别**：这里是数据库行的投影（`id` / `quantity` /
 * `updatedAt`），没有商品名与价格。`POST /shelves/:id/items`、
 * `PATCH /shelf-items/:id`、`POST /shelf-items/:id/adjust-quantity` 返回的都是它，
 * 界面要显示商品名需重新拉网格——因此单元格编辑器用的是**网格里的格子**
 * （`GridCellView`），不是这个形状。
 */
export interface ShelfItemView {
  id: string;
  shelfId: string;
  locationId: string;
  productId: string | null;
  schemeId: string | null;
  quantity: number;
  purchaseLimit: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** 货架资源本体（契约 §7.2 的 201、§7.3 的 200、§7.4 的 `createdShelves[]` 元素） */
export interface ShelfResourceView extends ShelfRegion {
  shelfId: string;
  code: string;
  label: string | null;
  iconUrl: string | null;
  items: ShelfItemView[];
}

/** `PUT|DELETE /shelves/:id/icon` 的响应（契约 §7 端点表第 11/12 行，最小自洽形状） */
export interface ShelfIconResult {
  shelfId: string;
  iconUrl: string | null;
}

/** `POST /shelves/:id/split` 的响应（契约 §7.4） */
export interface SplitShelfResult {
  deletedShelfId: string;
  createdShelves: ShelfResourceView[];
}

/**
 * 地点货架端点（`04-api-contract.md` §7 端点表 4–17）。
 *
 * 请求体一律 `Record<string, unknown>`：字段的权威校验在服务端 DTO，
 * 前端再抄一份只会多出一份会漂移的事实来源（与 `catalog.ts` 同一约定）。
 */
export const shelvesApi = {
  getGrid: (locationId: string) =>
    apiClient.get<LocationGridView>(`/locations/${locationId}/grid`),

  listShelves: (locationId: string) =>
    apiClient.get<ShelfResourceView[]>(`/locations/${locationId}/shelves`),

  /** 合并相邻格子创建货架（契约 §7.2）。`code` 缺省时由服务端按位置生成 */
  createShelf: (locationId: string, body: Record<string, unknown>) =>
    apiClient.post<ShelfResourceView>(`/locations/${locationId}/shelves`, body),

  getShelf: (shelfId: string) => apiClient.get<ShelfResourceView>(`/shelves/${shelfId}`),

  updateShelf: (shelfId: string, body: Record<string, unknown>) =>
    apiClient.patch<ShelfResourceView>(`/shelves/${shelfId}`, body),

  splitShelf: (shelfId: string) => apiClient.post<SplitShelfResult>(`/shelves/${shelfId}/split`),

  deleteShelf: (shelfId: string) => apiClient.del<{ success: true }>(`/shelves/${shelfId}`),

  /**
   * 设置货架图标（契约 §7 端点表第 11 行）。
   *
   * 字段名取 `iconUrl`：契约只写了「设置货架图标」、没有给请求体，
   * 而所有货架响应用的都是 `iconUrl`（§7.1 `:857`、§7.2 `:981`）。
   * 用响应里的同名字段是唯一不会新造词汇的选择。
   */
  setIcon: (shelfId: string, iconUrl: string) =>
    apiClient.put<ShelfIconResult>(`/shelves/${shelfId}/icon`, { iconUrl }),

  /** 清除图标：响应与 PUT 对称（`iconUrl: null`），便于调用方直接就地更新状态 */
  deleteIcon: (shelfId: string) => apiClient.del<ShelfIconResult>(`/shelves/${shelfId}/icon`),

  addItem: (shelfId: string, body: Record<string, unknown>) =>
    apiClient.post<ShelfItemView>(`/shelves/${shelfId}/items`, body),

  updateItem: (shelfItemId: string, body: Record<string, unknown>) =>
    apiClient.patch<ShelfItemView>(`/shelf-items/${shelfItemId}`, body),

  adjustQuantity: (shelfItemId: string, body: { delta: number; reason: string }) =>
    apiClient.post<ShelfItemView>(`/shelf-items/${shelfItemId}/adjust-quantity`, body),

  removeItem: (shelfItemId: string) =>
    apiClient.del<{ success: true }>(`/shelf-items/${shelfItemId}`),
};
